import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { NotesModalFormValues } from '../../aml/NotesModal';
import { Button, ButtonGroup, Col, Row } from 'react-bootstrap';
import { SalvButton, SalvModal, Size } from '@salvapp/ui-components';
import FormField from '../../form/FormField';
import * as Yup from 'yup';
import { FormikState } from 'formik/dist/types';
import { ApiBridgeAlert, ApiNote, ApiSanctionAlert, ApiSanctionEntityType } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/store';
import {
  InternalApiAlertType,
  InternalApiEntityType,
  InternalApiManualAlert,
  InternalApiMonitoringAlert,
  InternalApiScreeningAlertStatus,
} from '../../api-internal';
import confirmStatus from './confirmStatus';
import { systemAlertStatusCodes } from '../../screening/alert-status/alertStatusesConstants';
import { AlertInfo } from '../../screening/alert/filters/ScreeningAlertFiltersResults';
import { parseErrorMessage } from '../errors/helpers';

type AlertType = ApiSanctionAlert | InternalApiMonitoringAlert | ApiBridgeAlert | InternalApiManualAlert;

interface StatusModalProps {
  showModal: boolean;
  onSubmit: (values: ApiNote, status: string) => Promise<void>;
  onClose: () => void;
  alertType: InternalApiAlertType;
  currentStatus?: string;
  alert?: AlertType;
  selectedAlerts?: AlertInfo[];
}

const validationSchema = Yup.object().shape({
  content: Yup.string().required('Required'),
});

interface StatusModalOption {
  name: string;
  value: string;
  warnOfFalsePositive: boolean;
  warnOfFinal: boolean;
}

const statusIsEnabledForType = (status: InternalApiScreeningAlertStatus, type: InternalApiAlertType) => {
  switch (type) {
    case InternalApiAlertType.MONITORING:
      return status.enabledMonitoring;
    case InternalApiAlertType.SCREENING:
      return status.enabledScreening;
    case InternalApiAlertType.BRIDGE:
      return !!status.enabledBridge;
    case InternalApiAlertType.MANUAL:
      return !!status.enabledManual;
  }
};

const filterStatusesBasedOnType = (
  alert: AlertType | undefined,
  alertType: InternalApiAlertType,
  statuses: Array<InternalApiScreeningAlertStatus>,
  selectedAlerts?: AlertInfo[]
): Array<InternalApiScreeningAlertStatus> => {
  if (!alert) {
    const alertTypes = selectedAlerts?.map((it) => it.alertType);
    const entityTypes = selectedAlerts?.map((it) => it.entity);
    return statuses
      .filter((status) => {
        return entityTypes?.every((type) =>
          status._configuration?.entityTypes?.includes(
            InternalApiEntityType[type as keyof typeof InternalApiEntityType]
          )
        );
      })
      .filter((status) => {
        return alertTypes?.every((type) => statusIsEnabledForType(status, type));
      });
  }

  let applicableStatuses = [...statuses];

  const getAlertEntityType = () => {
    switch (alertType) {
      case InternalApiAlertType.MONITORING:
        return (alert as InternalApiMonitoringAlert).transaction
          ? InternalApiEntityType.TRANSACTION
          : InternalApiEntityType.PERSON;
      case InternalApiAlertType.SCREENING:
        return (alert as ApiSanctionAlert).entity.entityType === ApiSanctionEntityType.TRANSACTION
          ? InternalApiEntityType.TRANSACTION
          : InternalApiEntityType.PERSON;
      case InternalApiAlertType.BRIDGE:
        return (alert as ApiBridgeAlert).transaction ? InternalApiEntityType.TRANSACTION : InternalApiEntityType.PERSON;
      case InternalApiAlertType.MANUAL:
        return (alert as InternalApiManualAlert).transaction
          ? InternalApiEntityType.TRANSACTION
          : InternalApiEntityType.PERSON;
      default:
        return InternalApiEntityType.PERSON;
    }
  };
  const entityFilter = (status: InternalApiScreeningAlertStatus) =>
    !!status._configuration?.entityTypes?.includes(getAlertEntityType());
  applicableStatuses = applicableStatuses.filter(entityFilter);

  if (alertType === InternalApiAlertType.MONITORING) {
    const alertSubtype = (alert as InternalApiMonitoringAlert).score[0].scenarioType!;

    const alertSubtypeFilter = (status: InternalApiScreeningAlertStatus) => {
      const monitoringConfig = status._configuration?.monitoring;
      return !!(monitoringConfig?.enabled && monitoringConfig?.restrictedTo?.includes(alertSubtype));
    };
    applicableStatuses = applicableStatuses.filter(alertSubtypeFilter);
    applicableStatuses.sort((first, second) => {
      const firstConfig = first._configuration!.monitoring!;
      const secondConfig = second._configuration!.monitoring!;

      return firstConfig.sortOrder! > secondConfig.sortOrder! ? 1 : -1;
    });
  } else if (alertType === InternalApiAlertType.SCREENING) {
    const alertSubtype = (alert as ApiSanctionAlert).type;

    const alertSubtypeFilter = (status: InternalApiScreeningAlertStatus) => {
      const screeningConfig = status._configuration?.screeningLists;
      return !!(screeningConfig?.enabled && screeningConfig?.restrictedTo?.includes(alertSubtype));
    };
    applicableStatuses = applicableStatuses.filter(alertSubtypeFilter);

    const customListId = (alert as ApiSanctionAlert).customListId;
    if (customListId) {
      const customListFilter = (status: InternalApiScreeningAlertStatus) => {
        const customListsConfig = status._configuration?.customLists;
        return !!(customListsConfig?.enabled && customListsConfig?.restrictedTo?.includes(customListId));
      };

      applicableStatuses = applicableStatuses.filter(customListFilter);
    }

    applicableStatuses.sort((first, second) => {
      const firstConfig = first._configuration!.screeningLists!;
      const secondConfig = second._configuration!.screeningLists!;

      return firstConfig.sortOrder! > secondConfig.sortOrder! ? 1 : -1;
    });
  } else if (alertType === InternalApiAlertType.BRIDGE) {
    const bridgeFilter = (status: InternalApiScreeningAlertStatus) => {
      const bridgeConfig = status._configuration?.bridge;
      return !!bridgeConfig?.enabled;
    };
    applicableStatuses = applicableStatuses.filter(bridgeFilter);
  } else if (alertType === InternalApiAlertType.MANUAL) {
    const manualFilter = (status: InternalApiScreeningAlertStatus) => {
      return !!status.enabledManual;
    };
    applicableStatuses = applicableStatuses.filter(manualFilter);
    applicableStatuses.sort((first, second) => {
      const firstConfig = first._configuration!.screeningLists!;
      const secondConfig = second._configuration!.screeningLists!;

      return firstConfig.sortOrder! > secondConfig.sortOrder! ? 1 : -1;
    });
  }

  return applicableStatuses;
};

const getAvailableStatuses = (
  alert: AlertType | undefined,
  alertType: InternalApiAlertType,
  statuses: Array<InternalApiScreeningAlertStatus>,
  currentStatus?: string,
  selectedAlerts?: AlertInfo[]
): Array<StatusModalOption> => {
  return filterStatusesBasedOnType(alert, alertType, statuses, selectedAlerts)
    .filter(
      (it) =>
        !it.isSystem ||
        (currentStatus === systemAlertStatusCodes.rfiReceived && it.code === systemAlertStatusCodes.rfiSent)
    )
    .map<StatusModalOption>((status) => {
      return {
        name: status.textName,
        value: status.code!,
        warnOfFalsePositive: status.isFinal && status.isImportant,
        warnOfFinal: status.isFinal,
      };
    });
};

export const StatusModal = ({
  showModal,
  onSubmit,
  onClose,
  alertType,
  currentStatus,
  alert,
  selectedAlerts,
}: StatusModalProps) => {
  const dispatch = useDispatch();
  const [selectedStatus, setSelectedStatus] = useState<StatusModalOption>();
  const textAreaRef: MutableRefObject<HTMLTextAreaElement | null> = useRef(null);
  useEffect(() => {
    if (showModal) {
      textAreaRef.current?.focus();
    }
  }, [showModal]);

  const { activeStatuses } = useSelector((state: AppState) => state.screeningAlertStatuses);

  const initialValues = {
    content: '',
  };

  const initialFormikState: Partial<FormikState<NotesModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (
    values: NotesModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<NotesModalFormValues>
  ) => {
    try {
      if (!selectedStatus) {
        return;
      }

      await confirmStatus({
        onSubmit: () => onSubmit(values, selectedStatus.value),
        warnOnFinal: selectedStatus.warnOfFinal,
        warnOnTruePositive: selectedStatus.warnOfFalsePositive,
        statusName: selectedStatus.name,
        dispatch: dispatch,
      });
      onClose();
      resetForm(initialFormikState);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
      {({ submitForm, isSubmitting }: FormikProps<NotesModalFormValues>) => {
        return (
          <SalvModal show={showModal} onHide={onClose}>
            <Form>
              <SalvModal.Header closeButton>Update status</SalvModal.Header>
              <SalvModal.Body>
                <Row>
                  <Col md={12}>
                    <FormField inputref={textAreaRef} type='textarea' name='content' rows={5} label='Comment' />
                  </Col>
                </Row>
                <ButtonGroup vertical className='w-100'>
                  {getAvailableStatuses(alert, alertType, activeStatuses, currentStatus, selectedAlerts).map(
                    (status) => (
                      <Button
                        key={status.value}
                        disabled={isSubmitting}
                        variant='link'
                        onClick={() => {
                          setSelectedStatus(status);
                          submitForm();
                        }}
                        className='text-start hover-button'
                      >
                        {status.name}
                      </Button>
                    )
                  )}
                </ButtonGroup>
              </SalvModal.Body>
              <SalvModal.Footer>
                <SalvButton variant='muted' onClick={onClose} label='Close' size={Size.MEDIUM} />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};
export default StatusModal;
